/**
 * Contact linkification for chat display (device pass 10: "any time a number
 * or email is shared it's linked for the other person"). Display-level only —
 * splits text into plain/linked segments for the RECEIVING side's render.
 * Deliberately conservative: emails, and phone-shaped runs of 7–15 digits.
 * No URLs (not the ask), and no records — classification and contact records
 * stay the composer path's job.
 */

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const PHONE_RE = /\+?\d[\d\s().-]{5,}\d/g;

/**
 * @param {string} text
 * @returns {Array<{text: string, href?: string}>} ordered segments; linked
 *   segments carry a tel:/mailto: href
 */
export const linkifyContactSegments = (text) => {
  if (!text || typeof text !== 'string') return [{ text: text || '' }];
  // Fast bail: emails need an @, phone runs need digits — plain-word bubbles
  // skip both regex scans (this runs per received bubble in the chat list)
  if (!/[@\d]/.test(text)) return [{ text }];

  const matches = [];
  let m;

  EMAIL_RE.lastIndex = 0;
  while ((m = EMAIL_RE.exec(text)) !== null) {
    matches.push({ start: m.index, end: m.index + m[0].length, href: `mailto:${m[0]}` });
  }

  PHONE_RE.lastIndex = 0;
  while ((m = PHONE_RE.exec(text)) !== null) {
    const digits = m[0].replace(/\D/g, '');
    if (digits.length < 7 || digits.length > 15) continue;
    // Digits inside an email address belong to the email match
    const overlaps = matches.some((x) => m.index < x.end && m.index + m[0].length > x.start);
    if (overlaps) continue;
    matches.push({
      start: m.index,
      end: m.index + m[0].length,
      href: `tel:${m[0].startsWith('+') ? '+' : ''}${digits}`,
    });
  }

  if (matches.length === 0) return [{ text }];

  matches.sort((a, b) => a.start - b.start);
  const segments = [];
  let pos = 0;
  matches.forEach(({ start, end, href }) => {
    if (start > pos) segments.push({ text: text.slice(pos, start) });
    segments.push({ text: text.slice(start, end), href });
    pos = end;
  });
  if (pos < text.length) segments.push({ text: text.slice(pos) });
  return segments;
};
